import type { Module } from "vuex";
import { z } from "zod";
import { apiPaths } from "@/dictionaries/api/paths";
import http from "@/utils/axios";
import { notify } from "@/utils/notify";

const schedule = z.object({
  change_date: z.string(),
  end_date: z.string(),
  meeting_interval: z.number(),
  days: z.array(
    z.object({
      day_of_week: z.number(),
      hours: z.array(
        z.object({
          text: z.string(),
          from: z.string(),
          to: z.string(),
        })
      ),
    })
  ),
});

const upcomingSchedulesStore = z.object({
  next_schedule_change_date: z.object({
    min: z.string(),
    max: z.string(),
  }),
  schedules: z.array(schedule),
});

export type UpcomingSchedulesStore = z.infer<typeof upcomingSchedulesStore>;
export type UpcomingSchedule = z.infer<typeof schedule>;

const defaultDays = Array.from({ length: 7 }, (_, idx) => ({
  day_of_week: idx + 1,
  hours: [{ from: "08:00", to: "16:00", text: "08:00 - 16:00" }],
}));

export const initialWorkingHours = {
  next_schedule_change_date: {
    min: "",
    max: "",
  },
  schedules: [
    {
      change_date: "",
      end_date: "",
      meeting_interval: 0,
      days: defaultDays,
    },
  ],
};

// This is necessary, we need a copy of initialWorkingHours, because we do not want to modify this variable
const initialState = { ...initialWorkingHours };

const module: Module<UpcomingSchedulesStore, unknown> = {
  namespaced: true,
  state: initialState,
  getters: {
    getUpcomingSchedules: (state) => state.schedules,
    getNextScheduleChangeDate: (state) => state.next_schedule_change_date,
  },
  mutations: {
    setUpcomingSchedules(state, newState: UpcomingSchedulesStore["schedules"]) {
      state.schedules = newState;
    },
    setNextScheduleChangeDate(
      state,
      newState: UpcomingSchedulesStore["next_schedule_change_date"]
    ) {
      state.next_schedule_change_date = newState;
    },
  },
  actions: {
    async fetchUpcomingSchedules(ctx) {
      try {
        const { data: response } = await http.get<unknown>(
          apiPaths.calendar.schedule.upcoming.exact
        );
        const data = upcomingSchedulesStore.parse(response);
        const schedules = data.schedules;
        const next_schedule_change_date = data.next_schedule_change_date;
        ctx.commit("setUpcomingSchedules", schedules);
        ctx.commit("setNextScheduleChangeDate", next_schedule_change_date);
      } catch {
        notify("error.response_error", { type: "danger" });
        return false;
      }
    },
  },
};

export default module;
